import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, tap } from 'rxjs';
import { environment } from '../../environments/environment';
import { StateCleanupService } from './state-cleanup.service';

export interface MoodleConnectionStatus {
  connected: boolean;
  api_url?: string;
  status?: string;
  source?: 'moodle_plugin' | 'lms_integrations';
  has_ws_token?: boolean;
  connected_at?: string;
}

export interface MoodleConnectResponse {
  status: string;
  site_name: string;
  api_url: string;
  message: string;
}

export interface CloudIntegrationStatus {
  provider: string;
  connected: boolean;
  account_email?: string;
  account_name?: string;
  connected_at?: string;
  status?: string;
}

@Injectable({
  providedIn: 'root',
})
export class IntegrationService {
  private baseUrl = `${environment.userBackend}`;

  moodleStatus$ = new BehaviorSubject<MoodleConnectionStatus>({ connected: false });
  loading$ = new BehaviorSubject(false);

  constructor(
    private http: HttpClient,
    private stateCleanupService: StateCleanupService
  ) {
    this.stateCleanupService.register(() => this.resetState());
  }

  getMoodleStatus() {
    this.loading$.next(true);
    return this.http
      .get<MoodleConnectionStatus>(`${this.baseUrl}/integrations/moodle/status`)
      .pipe(
        tap((res) => {
          this.moodleStatus$.next(res);
          this.loading$.next(false);
        })
      );
  }

  connectMoodle(moodleUrl: string, wsToken: string) {
    this.loading$.next(true);
    return this.http
      .post<MoodleConnectResponse>(`${this.baseUrl}/integrations/moodle/connect`, {
        moodle_url: moodleUrl,
        ws_token: wsToken,
      })
      .pipe(
        tap((res) => {
          this.moodleStatus$.next({
            connected: true,
            api_url: res.api_url,
            status: 'active',
          });
          this.loading$.next(false);
        })
      );
  }

  disconnectMoodle() {
    this.loading$.next(true);
    return this.http
      .delete<any>(`${this.baseUrl}/integrations/moodle`)
      .pipe(
        tap(() => {
          this.moodleStatus$.next({ connected: false });
          this.loading$.next(false);
        })
      );
  }

  getCloudAuthUrl(provider: string): Observable<{ auth_url: string }> {
    return this.http.get<{ auth_url: string }>(`${this.baseUrl}/oauth/${provider}/authorize`);
  }

  getCloudStatus(provider: string, verify = false): Observable<CloudIntegrationStatus> {
    const params = verify ? { verify: 'true' } : {};
    return this.http.get<CloudIntegrationStatus>(`${this.baseUrl}/integrations/cloud/${provider}/status`, { params });
  }

  getAllCloudIntegrations(): Observable<CloudIntegrationStatus[]> {
    return this.http.get<CloudIntegrationStatus[]>(`${this.baseUrl}/integrations/cloud`);
  }

  disconnectCloud(provider: string): Observable<any> {
    return this.http.delete(`${this.baseUrl}/integrations/cloud/${provider}/disconnect`);
  }

  testCloudConnection(provider: string): Observable<CloudIntegrationStatus> {
    return this.getCloudStatus(provider, true);
  }

  private resetState() {
    this.moodleStatus$.next({ connected: false });
    this.loading$.next(false);
  }
}
